import React, { useMemo } from "react";
import { useLensesV2Context } from "../LensesV2Context";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { toTagCountMap } from "../utils";
import { assembleSentiment, deriveSentimentCard, selectSentimentItems } from "./utils";
import { SentimentCard, SentimentCardSkeleton, SentimentCardError } from "./SentimentCard";

// Sentiment widget — positive / negative / neutral, derived from the SIGNALS
// (each signal's polarity comes from config, resolved on the backend from
// BASE_TAXONOMY). We do NOT depend on emotion/affect tags. Counts come from
// plain per-tag aggregations (a sentiment rollup needs no pairs — cooccurrence
// would be the wrong tool): a dedicated current-period aggrMetrics call, plus
// the previous-period one shared with the Signals/Topics widgets (identical args
// → shared react-query cache). Renders the same card UI as the v1 dashboard.
export const SentimentWidget = () => {
  const { findQuery, prevFindQuery, signals, emotionAffect } = useLensesV2Context();

  const currCounts = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: ["annotations.tags.tags"],
    findQuery,
    vizCardId: "lensV2CurrTagCounts",
    queryOptions: { enabled: !!findQuery },
  });

  const prevCounts = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: ["annotations.tags.tags"],
    findQuery: prevFindQuery,
    vizCardId: "lensV2PrevTagCounts",
    queryOptions: { enabled: !!prevFindQuery },
  });

  const card = useMemo(() => {
    // The tag set + polarity lookup are chosen by CONFIG.source (signals vs
    // emotion & affect); everything downstream is identical.
    const items = selectSentimentItems({ signals, emotionAffect });
    const buckets = assembleSentiment(
      items,
      toTagCountMap(currCounts.data),
      toTagCountMap(prevCounts.data)
    );
    return deriveSentimentCard(buckets);
  }, [signals, emotionAffect, currCounts.data, prevCounts.data]);

  if (currCounts.isLoading) return <SentimentCardSkeleton />;
  if (currCounts.isError) {
    return <SentimentCardError message={currCounts.error?.response?.data?.message || currCounts.error?.message} />;
  }

  return <SentimentCard {...card} />;
};
